import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Body from '../Components/layout/Body';
import { Row, Col, Card, CardImg, CardText, CardBody, CardTitle, CardSubtitle, Button, Alert, ListGroup, ListGroupItem } from 'reactstrap';
import LearnMoreModal from '../Components/layout/LearnMoreModal';
import { CAT_ARRAY, getMethodsForCategory, getAccessMethodNameForID, LOWTECH_CAT_ARRAY } from './ConstAccessMethodsMock';
import { FIRST_METHOD_CATEGORY } from './ConstForTestDriveSection';
import TestDriveDevSidebar from './TestDriveDevSidebar';

const TD_CHOOSE_METHOD_HELP = `
  <div>
    <p><strong>Purpose: </strong>Here we get into the first main step of defining what, exactly, you are going to Test-drive.  
    Here you’ll choose the general type of access method for the Test-drive. 
    (In subsequent steps, you’ll define exactly how you are going to set up this access method.) 
    </p>
    <p>
    Your goal is to use all the information you’ve gathered so far, and choose something that is a good fit for the client’s abilities as well as their goals and needs.
    </p>
    <p>
    By default, this page shows all of the access methods in the app. 
    Note that <strong>these are not intended to be specific products</strong> – some of the photos may show a specific product but that is just to show an example. 
    We hope this covers anything you might try, but if you don’t see what you’re looking for, just select the closest thing to it.   
    </p>
    <p>
    If you want to reduce the number of options on the page, you can select the category – Keyboards, Touchscreen access, Speech recognition, Eye-gaze access, Pointing devices, Switch access. 
    </p>
    <p>
    <strong>What if your client needs more than one type of device?</strong>  Some clients need a combination of access methods to make a complete solution.  
    For example, a client might use speech recognition for most text entry, along with a trackball for most mouse functions.  
    We suggest you Test-drive one access method at a time, to focus your decision-making.  
    In fact, you can only choose one option at a time on this page.   
    Later, if you want to do a Test-drive that combines multiple methods, choose the one that’s primary here, and make a note in the setup section about any complementary methods. 
    </p>
  </div>
`;
const NO_INFO = 'No information available';

const propTypes = {
  firstName: PropTypes.string,
  firstMethodCategory: PropTypes.string,
  activeAccessMethodID: PropTypes.string,
  onChangeActiveAccessMethodID: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  firstMethodCategory: '',
  activeAccessMethodID: '',
};

//Details markup is the raw HTML that is sent to <LearnMore> component and displayed in the modal for each access method.
//This must be raw HTML (not JSX) since that is what <LearnMore> requires.
//Use Bootstrap layout to be responsive to different screen widths
function getDetailsMarkup(accessMethod){
  // return AM_PROFILE_MOCKT;
  return(`
  <div class="container-fluid">
    ${getOverviewMarkup( accessMethod)}
    <hr />
    ${getConsiderationsMarkup( accessMethod)}
    <hr />
    ${getAttribMarkup( accessMethod.attr)}
  </div>
  `);
} 
function getOverviewMarkup( accessMethod){
  console.log("accessMethod.attr: " + accessMethod.attr);
  return(`
    <div class="row">
      <div class="col-md-3"><img class="aa-modal-image" src=${accessMethod.imgSrc} /></div>
      <div class="col-md-9">
        <p>
          ${(accessMethod.longDesc) ? accessMethod.longDesc : NO_INFO}
        </p>  
      </div>
    </div>
  `);
}
function getAttribMarkup( attr){
  let attribMarkup='';
  if( attr){
    attribMarkup = '<p><small>' + attr + '</small></p>';
  }
  return( attribMarkup);
}
function getConsiderationsMarkup( accessMethod){
  return(`
  <div>
    <h5>Things to Consider</h5>
    ${getTasksMarkup( accessMethod)}
    ${getSetupMarkup( accessMethod)}
    ${getUserMarkup( accessMethod)}
  </div>
  `);
}
function getTasksMarkup( accessMethod){
  let taskItems = [];
  let textEntryItems = accessMethod.textEntryItems ?? [];
  let navigationItems = accessMethod.navigationItems ?? [];
  taskItems = taskItems.concat(textEntryItems, navigationItems);

  let heading = 'Tasks';
  return(getSectionMarkup(taskItems, heading));
}
function getUserMarkup( accessMethod){
  //deal with undefined or null values for considerations
  //(considerations could also come in as an empty array, potentially)
  let userItems = [];
  let motorItems = accessMethod.motorConsiderations ?? [];
  let visualItems = accessMethod.visualConsiderations ?? [];
  let cognitiveItems = accessMethod.cognitiveConsiderations ?? [];
  userItems = userItems.concat(motorItems, visualItems, cognitiveItems);

  let heading = 'User';
  return(getSectionMarkup(userItems, heading));
}
function getSetupMarkup( accessMethod){
  let setupItems = accessMethod.setupConsiderations ?? [];
  let heading = 'Setup';
  return(getSectionMarkup(setupItems, heading));
}
function getProductMarkup( accessMethod){
  let productItems = accessMethod.productConsiderations ?? [];
  let heading = 'Available products';
  return(getSectionMarkup(productItems, heading));
}

function getSectionMarkup(strArray, heading){
  return(`
    <div>
      <h6 class="aa-list-header">${heading || ''}</h6>
      ${getListFromArray(strArray, true)}
    </div>
  `);

}
//takes an array of strings, and converts it to HTML markup for
//an unordered list, where each list item is a string in the array.
//Set fallback=true to returns a NO_INFO <p> if the strArray is empty or otw falsy
function getListFromArray(strArray, fallback){
  let listMarkup = '';
  if(strArray && strArray.length > 0){
    listMarkup = 
      '<ul>' +
      strArray.map((item) => {
          return (item ? '<li>'+item+'</li>' : '');
      }).join('') +
      '</ul>';
  }
  else if(fallback){
    listMarkup = '<p>' + NO_INFO + '</p>';
  }
  return( listMarkup);
}

//content sub-component with unique content for this page
function TestDriveChooseMethodContent(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const showLowTechMethods = props.showLowTechMethods;
  const firstMethodCategory = props.firstMethodCategory;
  const activeAccessMethodID = props.activeAccessMethodID;
  const onChangeActiveAccessMethodID = props.onChangeActiveAccessMethodID;
  const selectedCat = props.selectedCat;

  console.log("rendering TestDriveChooseMethod Content, selectedCat: " + selectedCat);

  //show the selected Category or if that's empty, show all the categories
  let allCategories = (showLowTechMethods ? LOWTECH_CAT_ARRAY : CAT_ARRAY)
  let categoriesToShow = allCategories.filter(cat => cat === selectedCat);
  if (categoriesToShow.length === 0){
    categoriesToShow = allCategories;
  }
  let numAccessMethods = 0;
  for (let i = 0; i < categoriesToShow.length; i++) {
    for (let j = 0; j < getMethodsForCategory(categoriesToShow[i]).length; j++) {
      numAccessMethods++;
    }
  }

  return(
    <>
    {(firstMethodCategory === FIRST_METHOD_CATEGORY.CURRENT) ?
      <p><strong>Note:</strong> To get a baseline with {firstName}'s current access method, go ahead and choose that method here. (If there's not an exact match, don't worry -- just pick something that's close and you can specify details later. And if there's nothing close, that's on us -- just skip this step and enter more details later.)</p>
      : <span></span>
    }
    {(firstMethodCategory === FIRST_METHOD_CATEGORY.STANDARD) ?
      <p><strong>Note:</strong> You mentioned that you'd like to get a baseline with a regular input method (like a regular keyboard or trackpad), so you may want to choose one of those here.</p>
      : <span></span>
    }
    {((firstMethodCategory === FIRST_METHOD_CATEGORY.ANY) || !firstMethodCategory) ?
      <p><strong>Note:</strong> Try to choose something that is a good fit for {firstName}'s abilities, as well as {pronouns[2]} priority activity and contexts of use.</p>
      : <span></span>
    }

    {/* Merge categories together: */}   
    <div>
      <Alert fade color={(activeAccessMethodID) ? 'success' : 'secondary'} timeout={150}>
        <strong>Your access method:</strong> {(activeAccessMethodID) ? getAccessMethodNameForID(activeAccessMethodID) : 'None selected'}
      </Alert>

      <TestDriveChooseMethodHeader
        showLowTechMethods={showLowTechMethods}
        selectedCat={selectedCat}
        numAccessMethods={numAccessMethods}
      />

      <Row className="aa-review-row">
        {categoriesToShow.map((cat, index) => {
          return(
            <React.Fragment key={index}>
            {getMethodsForCategory(cat).map(function(accessMethod, ix) {
              return(
              <Col key={ix+cat} xl="3" lg="4" sm="6" className="mb-4">
                <Card id={accessMethod.id} role='radio' className={(activeAccessMethodID===accessMethod.id) ? 'selected h-100' : 'h-100'} >
                  <CardImg src={accessMethod.imgSrc} alt={accessMethod.imgAlt}/>
                  <CardBody>
                    <div className="aa-card-text-block">
                      <CardTitle tag="h5">
                        {(activeAccessMethodID===accessMethod.id) 
                          ? <span className="fa fa-check-circle aa-success"></span>
                          : ''} 
                          {accessMethod.name}</CardTitle>
                      {(accessMethod.subTitle)
                        ? <CardSubtitle tag="h6" className="mb-2 text-muted">{accessMethod.subTitle}</CardSubtitle>
                        : <span></span>
                      }
                      <CardText>{accessMethod.shortDesc}</CardText>
                    </div>
                    <div className="aa-card-buttons">
                    <Button id={"button-"+accessMethod.id} 
                      color={(activeAccessMethodID===accessMethod.id) ? "secondary" : "primary"}
                      outline={(activeAccessMethodID===accessMethod.id)}
                      onClick={onChangeActiveAccessMethodID}>
                      {(activeAccessMethodID===accessMethod.id) ? "Clear" : "Choose"}
                    </Button>
                    <LearnMoreModal 
                        buttonClassName="float-end"
                        linkText="details&gt;&gt;"
                        title={accessMethod.name}
                        content={getDetailsMarkup(accessMethod)}
                      />
                      </div>
                  </CardBody>
                </Card>
              </Col>
              );
            })}
            </React.Fragment>
          );
        })}
        </Row>
      </div>

    <p><strong>Note: </strong>This listing shows representative types of access devices, not specific products. You can enter the exact product name later, if you want.</p>
    <hr />
    </>
  );
}

function TestDriveChooseMethodHeader(props){
  const {
    showLowTechMethods,
    selectedCat,
    numAccessMethods,
  } = props;
  let allHeading = showLowTechMethods ? 'All Low-tech Methods' : 'All Methods';

  return(
    <Row>
      <Col>
        <div className="aa-method-header border-bottom">
          <span className="aa-method-header-name">
            <strong>{selectedCat ? selectedCat : allHeading}</strong>
          </span>
          <span className="float-end">{numAccessMethods} item{numAccessMethods !== 1 ? 's' : null}</span>
        </div>
      </Col>
    </Row>
  );
}

function TestDriveChooseMethodSidebar(props) {
  const {
    isLowTechPath,
    showLowTechMethods,
    onChangeShowLowTechMethods,
    selectedCat,
    onChangeSelectedCat
  } = props;

  let categories = showLowTechMethods ? LOWTECH_CAT_ARRAY : CAT_ARRAY;
  let heading = showLowTechMethods ? 'All Low-tech Methods' : 'All Methods';
  let toggleLabel = showLowTechMethods ? 'show all methods' : 'low-tech only';

  return(
    <ListGroup>
      <ListGroupItem>
        <strong>View By Category:</strong>
        <ListGroup flush className="aa-sidebar-links">
          <ListGroupItem key={"head"}>
            <Button color={"link"} className={`${selectedCat === '' ? "active" : ''}`}
            onClick={() => onChangeSelectedCat('')}>
              {heading}
            </Button>
          </ListGroupItem>
          {categories.map((cat, index) => {
            return(
            <ListGroupItem key={index}>
              <Button color={"link"} className={`${selectedCat === cat ? "active" : ''}`} onClick={() => onChangeSelectedCat(cat)}>
                {cat}
              </Button>
            </ListGroupItem>
            );
          })}
        </ListGroup>
      </ListGroupItem>
      {isLowTechPath 
        ?
        <ListGroupItem>
          <Button color={"link"}
            onClick={() => {onChangeShowLowTechMethods(!showLowTechMethods); onChangeSelectedCat('');}}>
            {toggleLabel}
          </Button>
        </ListGroupItem>
        : <></>
      }
    </ListGroup>
  )
  //the original markup, before stacking with TestDriveDevSidebar
  // return (
  //   <>
  //   <p className="aa-sidebar-heading"><strong>View By Category:</strong></p>
  //   <List type="unstyled" className="aa-sidebar-links">
  //     <li key={"head"}>
  //       <Button color={"link"} className={`${selectedCat === '' ? "active" : ''}`}
  //       onClick={() => onChangeSelectedCat('')}>
  //         {heading}
  //       </Button>
  //     </li>
  //     {categories.map((cat, index) => {
  //       return(
  //       <li key={index}>
  //         <Button color={"link"} className={`${selectedCat === cat ? "active" : ''}`} onClick={() => onChangeSelectedCat(cat)}>
  //           {cat}
  //         </Button>
  //       </li>
  //       );
  //     })}
  //   </List>
    // // {isLowTechPath ?
    // //     <Button color={"link"}
    // //       onClick={() => {onChangeShowLowTechMethods(!showLowTechMethods); onChangeSelectedCat('');}}>
    // //       {toggleLabel}
    // //     </Button>
    // //   : <span></span>
    //   }
  //   </>
  // );
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function TestDriveChooseMethod(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const isLowTechPath = props.isLowTechPath;
  const firstMethodCategory = props.firstMethodCategory //TODO: to influence order of presentation (not yet used)
  const activeAccessMethodID = props.activeAccessMethodID;
  const onChangeActiveAccessMethodID = props.onChangeActiveAccessMethodID;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;
  const dev = props.dev || {};

  //the user-selected access-method category
  const [selectedCat, setSelectedCat] = useState('');
  //a state we can toggle to show all methods vs. only low-tech methods
  const [showLowTechMethods, setShowLowTechMethods] = useState(isLowTechPath);

  const pageData = {
    image: "./assets/images/target-icon.jpg",
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol test-drive",
    title: `Choose an Access Method to try`,
    subTitle: 'The first step of a Test-drive is to choose the access method you want to try.',
    learnMore: {linkText: "Learn More about choosing appropriate access methods",
                title: "Choosing an access method",
                content: TD_CHOOSE_METHOD_HELP
                },
    nextRoute: "",
    sidebar: true,
    rightMargin: false
  }

  console.log("Rendering TestDriveChooseMethod, lowTechPath: " + isLowTechPath + " state.showLowTechMethods: " + showLowTechMethods);

  useEffect(() => {
    return () => {
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <TestDriveChooseMethodContent 
          firstName = {firstName}
          pronouns = {pronouns}
          showLowTechMethods = {showLowTechMethods}
          firstMethodCategory = {firstMethodCategory}
          activeAccessMethodID = {activeAccessMethodID}
          onChangeActiveAccessMethodID = {onChangeActiveAccessMethodID}
          selectedCat = {selectedCat}
          onChangeSelectedCat = {setSelectedCat}
        />
      }
      sidebarComponent={
        // dev.showDevSidebar 
        // ?
        // <TestDriveDevSidebar 
        //   activeTestDriveId={dev.tdId}
        //   activeTestDrive={dev.td}
        //   activeTestDriveActivityId={dev.actId}
        //   activeTestDriveActivities={dev.acts}
        //   completedTestDrives={dev.ctds}
        // />  
        // :
        <>
        <TestDriveChooseMethodSidebar
          isLowTechPath = {isLowTechPath}
          showLowTechMethods = {showLowTechMethods}
          onChangeShowLowTechMethods = {setShowLowTechMethods}
          selectedCat = {selectedCat}
          onChangeSelectedCat = {setSelectedCat}
        />
        {/* <hr/> */}
        <TestDriveDevSidebar 
          activeTestDriveId={dev.tdId}
          activeTestDrive={dev.td}
          activeTestDriveActivityId={dev.actId}
          activeTestDriveActivities={dev.acts}
          completedTestDrives={dev.ctds}
        />  
      </>
      }
      onContinue={onContinue}  
    />
    </>
  );
}

TestDriveChooseMethod.propTypes = propTypes;
TestDriveChooseMethod.defaultProps = defaultProps;

export default TestDriveChooseMethod;